import { Link } from 'react-router-dom';
import { Encabezado } from '../navegacion/Marco';
import { Cargando, ErrorEnPantalla, Insignia, Panel, Vacio, clases } from '../comun/componentes';
import { useDatos } from '../comun/useDatos';
import { gs, numero, porcentaje } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import type { AgendaDiaria, HabilitacionVista, Tablero } from '../api/tipos';
import { hoyIso } from '../comun/formato';

/**
 * Inicio: lo que el titular mira una vez por día. Arranca simple —cinco números y tres
 * listas— y cada tarjeta lleva a la pantalla donde se resuelve lo que muestra.
 */
export function Inicio() {
  const { usuario } = useSesion();
  const hoy = hoyIso();

  const tablero = useDatos<Tablero>('/api/v1/reportes/tablero');
  const agenda = useDatos<AgendaDiaria>(`/api/v1/agenda/dia?dia=${hoy}`);
  const vencimientos = useDatos<HabilitacionVista[]>('/api/v1/cumplimiento/vencimientos');

  return (
    <>
      <Encabezado
        titulo={`Buen día, ${usuario?.nombreCompleto.split(',').pop()?.trim() ?? ''}`}
        detalle={usuario?.clinica}
      />

      {tablero.cargando && <Cargando que="el tablero" />}
      {tablero.error && <ErrorEnPantalla mensaje={tablero.error} reintentar={tablero.recargar} />}

      {tablero.datos && (
        <>
          <div className="grid gap-3 tablet:grid-cols-2 portatil:grid-cols-4">
            <Indicador
              rotulo="Producción del mes"
              valor={gs(tablero.datos.produccionGs)}
              detalle="Presupuestos aceptados"
              a="/finanzas/reportes"
            />
            <Indicador
              rotulo="Cobrado"
              valor={gs(tablero.datos.cobradoGs)}
              detalle={`${tablero.datos.citasAtendidas} citas atendidas`}
              a="/finanzas/caja"
            />
            <Indicador
              rotulo="Por cobrar"
              valor={gs(tablero.datos.porCobrarGs)}
              detalle="Saldo de planes vigentes"
              a="/finanzas/cuentas"
              tono={tablero.datos.porCobrarGs > tablero.datos.cobradoGs ? 'aviso' : 'neutro'}
            />
            <Indicador
              rotulo="Ausentismo"
              valor={porcentaje(tablero.datos.tasaAusentismo)}
              detalle={`${tablero.datos.citasAgendadas} citas agendadas`}
              a="/finanzas/reportes"
              tono={tablero.datos.tasaAusentismo > 15 ? 'alerta' : 'neutro'}
            />
          </div>

          <div className="mt-3 grid gap-3 portatil:grid-cols-3">
            <Atajo
              a="/clinica/cumplimiento"
              rotulo="Vencimientos"
              cantidad={tablero.datos.vencimientosProximos}
              tono={tablero.datos.vencimientosProximos > 0 ? 'alerta' : 'exito'}
              detalle="Habilitaciones por renovar"
            />
            <Atajo
              a="/clinica/inventario"
              rotulo="Inventario"
              cantidad={tablero.datos.alertasInventario}
              tono={tablero.datos.alertasInventario > 0 ? 'aviso' : 'exito'}
              detalle="Insumos bajo mínimo o vencidos"
            />
            <Atajo
              a="/seguimientos"
              rotulo="Seguimientos"
              cantidad={tablero.datos.seguimientosPendientes}
              tono={tablero.datos.seguimientosPendientes > 0 ? 'marca' : 'exito'}
              detalle="Pacientes para contactar"
            />
          </div>
        </>
      )}

      <div className="mt-4 grid gap-4 portatil:grid-cols-3">
        <Panel
          className="portatil:col-span-2"
          titulo="Agenda de hoy"
          descripcion={agenda.datos?.feriados.length ? agenda.datos.feriados.join(' · ') : undefined}
          accion={
            <Link to="/agenda" className="text-sm font-medium text-marca-700 underline-offset-2 hover:underline">
              Ver agenda
            </Link>
          }
        >
          {agenda.cargando && <Cargando que="la agenda" />}
          {agenda.error && <ErrorEnPantalla mensaje={agenda.error} reintentar={agenda.recargar} />}
          {agenda.datos && agenda.datos.citas.length === 0 && (
            <Vacio
              titulo="No hay citas para hoy"
              detalle="Cuando agendes la primera, va a aparecer acá con su hora y su profesional."
            />
          )}
          {agenda.datos && agenda.datos.citas.length > 0 && (
            <ul className="divide-y divide-papel-200">
              {agenda.datos.citas.slice(0, 8).map((cita) => (
                <li key={cita.id} className="flex items-center gap-3 py-2">
                  <span className="cifra w-12 shrink-0 text-sm font-semibold text-tinta-900">
                    {cita.inicioLocal.slice(11, 16)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <Link
                      to={`/pacientes/${cita.pacienteId}`}
                      className="block truncate text-sm font-medium text-tinta-800 hover:text-marca-700"
                    >
                      {cita.paciente}
                    </Link>
                    <span className="block truncate text-dato text-tinta-500">
                      {cita.profesional}
                      {cita.motivoConsulta ? ` · ${cita.motivoConsulta}` : ''}
                    </span>
                  </span>
                  <EstadoDeCita estado={cita.estado} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel titulo="Qué se vence" descripcion="Habilitaciones y licencias">
          {vencimientos.cargando && <Cargando que="los vencimientos" />}
          {vencimientos.datos && vencimientos.datos.length === 0 && (
            <Vacio titulo="Todo al día" detalle="Ninguna habilitación entra en la ventana de aviso." />
          )}
          {vencimientos.datos && vencimientos.datos.length > 0 && (
            <ul className="space-y-3">
              {vencimientos.datos.map((papel) => (
                <li key={papel.id}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-tinta-800">{papel.titulo}</p>
                    <Insignia tono={papel.estado === 'Vencida' ? 'alerta' : 'aviso'}>
                      {papel.estado === 'Vencida'
                        ? `venció hace ${Math.abs(papel.diasParaVencer ?? 0)} d`
                        : `${papel.diasParaVencer} d`}
                    </Insignia>
                  </div>
                  <p className="text-dato text-tinta-500">{papel.numero}</p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {tablero.datos && tablero.datos.prestacionesTop.length > 0 && (
        <Panel className="mt-4" titulo="Lo que más se hizo" descripcion="En el período del tablero">
          <ul className="grid gap-2 tablet:grid-cols-2 portatil:grid-cols-3">
            {tablero.datos.prestacionesTop.map((prestacion) => (
              <li
                key={prestacion.prestacionId}
                className="flex items-baseline justify-between gap-3 rounded-lg bg-papel-100 px-3 py-2"
              >
                <span className="truncate text-sm text-tinta-700">{prestacion.prestacion}</span>
                <span className="cifra shrink-0 text-sm font-semibold text-tinta-900">
                  {numero(prestacion.veces)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </>
  );
}

function Indicador({
  rotulo,
  valor,
  detalle,
  a,
  tono = 'neutro',
}: {
  rotulo: string;
  valor: string;
  detalle: string;
  a: string;
  tono?: 'neutro' | 'aviso' | 'alerta';
}) {
  return (
    <Link
      to={a}
      className={clases(
        'panel block p-4 transition-shadow hover:shadow-elevado',
        tono === 'aviso' && 'ring-1 ring-aviso-200',
        tono === 'alerta' && 'ring-1 ring-alerta-200',
      )}
    >
      <p className="etiqueta-campo">{rotulo}</p>
      <p className="cifra mt-1 text-xl font-semibold text-tinta-900 tablet:text-2xl">{valor}</p>
      <p className="mt-1 text-dato text-tinta-500">{detalle}</p>
    </Link>
  );
}

function Atajo({
  a,
  rotulo,
  cantidad,
  detalle,
  tono,
}: {
  a: string;
  rotulo: string;
  cantidad: number;
  detalle: string;
  tono: 'exito' | 'aviso' | 'alerta' | 'marca';
}) {
  return (
    <Link to={a} className="panel flex items-center gap-4 p-4 transition-shadow hover:shadow-elevado">
      <span
        className={clases(
          'cifra grid h-12 w-12 shrink-0 place-items-center rounded-xl text-lg font-bold',
          tono === 'alerta' && 'bg-alerta-50 text-alerta-700',
          tono === 'aviso' && 'bg-aviso-50 text-aviso-700',
          tono === 'exito' && 'bg-exito-50 text-exito-700',
          tono === 'marca' && 'bg-marca-50 text-marca-700',
        )}
      >
        {cantidad}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-tinta-800">{rotulo}</span>
        <span className="block text-dato text-tinta-500">{detalle}</span>
      </span>
    </Link>
  );
}

export const TONO_CITA: Record<string, 'neutro' | 'marca' | 'exito' | 'aviso' | 'alerta'> = {
  Programada: 'neutro',
  Confirmada: 'marca',
  EnSala: 'aviso',
  EnAtencion: 'aviso',
  Atendida: 'exito',
  Cancelada: 'neutro',
  Ausente: 'alerta',
  Reprogramada: 'neutro',
};

export function EstadoDeCita({ estado }: { estado: string }) {
  return <Insignia tono={TONO_CITA[estado] ?? 'neutro'}>{estado.replace(/([A-Z])/g, ' $1').trim()}</Insignia>;
}
